import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Trash2, FileSpreadsheet, CalendarDays, Lock } from "lucide-react";
import { api, money2, today, errorText, downloadReport, TX_TYPES, KIND_DEFAULT_CATEGORY, CONTACT_KIND, INFLOW, NEEDS_CONTACT, TX_LABEL } from "@/lib/api";
import { CategorySelect, catOf } from "./categories";
import { PaymentSelect, encodePay, decodePay } from "./payment";
import { ContactSelect } from "./contactPicker";

const addDays = (d, n) => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const weekStart = (d) => addDays(d, -((new Date(`${d}T12:00:00`).getDay() + 6) % 7));
const WEEKDAY = new Intl.DateTimeFormat("tr-TR", { weekday: "short" });
const LONG = new Intl.DateTimeFormat("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const fmtDay = (d, f) => f.format(new Date(`${d}T12:00:00`));

const DayStrip = ({ date, setDate, days }) => {
  const start = weekStart(date);
  const week = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="day-strip" data-testid="ledger-day-strip">
      <button className="strip-nav" onClick={() => setDate(addDays(date, -7))} data-testid="ledger-prev-week"><ChevronLeft size={18} /></button>
      {week.map(d => {
        const info = days.find(x => x.date === d);
        const net = info ? info.income - info.expense : 0;
        return (
          <button key={d} className={`day-cell ${d === date ? "active" : ""} ${d === today() ? "is-today" : ""}`} onClick={() => setDate(d)} data-testid={`ledger-day-${d}`}>
            <small>{fmtDay(d, WEEKDAY)}</small><strong>{Number(d.slice(8))}</strong>
            <em className={net > 0 ? "pos" : net < 0 ? "neg" : ""}>{info ? money2(net).replace(",00", "") : "—"}</em>
          </button>
        );
      })}
      <button className="strip-nav" onClick={() => setDate(addDays(date, 7))} data-testid="ledger-next-week"><ChevronRight size={18} /></button>
    </div>
  );
};

const emptyRow = { kind: "expense", title: "", income: "", expense: "", category: "Genel", contact: "", pay: "", notes: "" };
const isOut = (k) => !INFLOW.includes(k);
const fromRow = (r) => (r ? { kind: r.type, title: r.title, income: isOut(r.type) ? "" : r.amount, expense: isOut(r.type) ? r.amount : "", category: r.category || "", contact: r.contact || "", pay: encodePay(r), notes: r.notes || "" } : emptyRow);
const fitCategory = (p, kind) => {
  const cat = catOf(p.category);
  if (cat?.kind && cat.kind !== kind) return KIND_DEFAULT_CATEGORY[kind];
  return (!p.category || p.category === "Genel") ? KIND_DEFAULT_CATEGORY[kind] : p.category;
};
const changeKind = (p, kind) => {
  const amount = p.income || p.expense;
  const pay = INFLOW.includes(kind) && /^(card|person):/.test(p.pay) ? "" : p.pay;
  return { ...p, kind, pay, category: fitCategory(p, kind), income: isOut(kind) ? "" : amount, expense: isOut(kind) ? amount : "" };
};
const LedgerRow = ({ row, index, lookups, onSave, onDelete , onContactAdded}) => {
  const [f, setF] = useState(fromRow(row));
  const first = useRef(null);
  const locked = row && row.type === "transfer";
  const dirty = JSON.stringify(f) !== JSON.stringify(fromRow(row));
  const id = row ? row.id : "new";
  const set = (k, v) => setF(p => {
    if (k === "kind") return changeKind(p, v);
    if (k === "category") { const cat = catOf(v); return cat?.kind ? changeKind({ ...p, category: v }, cat.kind) : { ...p, category: v }; }
    const next = { ...p, [k]: v };
    if (k === "income" && v) { const kind = isOut(p.kind) ? "income" : p.kind; Object.assign(next, { expense: "", kind, category: fitCategory(p, kind), pay: /^(card|person):/.test(p.pay) ? "" : p.pay }); }
    if (k === "expense" && v) { const kind = isOut(p.kind) ? p.kind : "expense"; Object.assign(next, { income: "", kind, category: fitCategory(p, kind) }); }
    return next;
  });
  const save = async () => {
    const amount = Number(f.income || f.expense);
    if (!f.title.trim() || !(amount > 0)) { toast.error("Açıklama ve gelir ya da gider tutarı girin"); return; }
    if ((NEEDS_CONTACT.includes(f.kind) || catOf(f.category)?.contact) && !f.contact) { toast.error(`${f.category || "Bu işlem"} için Cari sütunundan bir cari seçin veya "+ Yeni cari ekle" ile ekleyin`); return; }
    const ok = await onSave({ title: f.title.trim(), amount, type: f.kind, category: f.category || "Genel", contact: f.contact, notes: f.notes, ...decodePay(f.pay), due_date: row?.due_date || "" }, row?.id);
    if (ok && !row) { setF(emptyRow); first.current?.focus(); }
  };
  const key = (e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") setF(fromRow(row)); };
  const cell = (k, props = {}) => <input value={f[k]} onChange={e => set(k, e.target.value)} onKeyDown={key} disabled={locked} data-testid={`ledger-${id}-${k}`} {...props} />;
  return (
    <tr className={`${dirty ? "dirty" : ""} ${row ? "" : "new-row"}`} data-testid={`ledger-row-${id}`}>
      <td className="row-no">{row ? index + 1 : "+"}</td>
      <td className={`kind-cell kind-${f.kind}`}>{locked ? <span className="locked-kind">Borç ödemesi</span> : <select value={f.kind} onChange={e => set("kind", e.target.value)} onKeyDown={key} data-testid={`ledger-${id}-kind`}>{TX_TYPES.map(([v]) => <option key={v} value={v}>{TX_LABEL[v]}</option>)}</select>}</td>
      <td>{cell("title", { ref: first, placeholder: row ? "" : "Yeni satır: açıklama yazın…" })}</td>
      <td className="num inc">{cell("income", { type: "number", step: "0.01", min: "0", placeholder: "0,00" })}</td>
      <td className="num exp">{cell("expense", { type: "number", step: "0.01", min: "0", placeholder: "0,00" })}</td>
      <td className="cat-cell"><CategorySelect value={f.category} onChange={v => set("category", v)} onKeyDown={key} disabled={locked} testId={`ledger-${id}-category`} /></td>
      <td><ContactSelect contacts={lookups.contacts} value={f.contact} onPick={v => set("contact", v)} txKind={f.kind} onAdded={onContactAdded} onKeyDown={key} disabled={locked} testId={`ledger-${id}-contact`} /></td>
      <td className="pay-cell"><PaymentSelect lookups={lookups} kind={f.kind} value={f.pay} onChange={v => set("pay", v)} onKeyDown={key} disabled={locked} testId={`ledger-${id}-pay`} /></td>
      <td>{cell("notes")}</td>
      <td className="row-tools">
        {locked ? <span title="Kart işlemi – Kredi Kartları sayfasından yönetilir"><Lock size={13} /></span> : <>
          {dirty && <button className="icon-only ok" onClick={save} title="Kaydet (Enter)" data-testid={`ledger-${id}-save`}><Check size={14} /></button>}
          {row && <button className="icon-only" onClick={() => onDelete(row.id)} title="Sil" data-testid={`ledger-${id}-delete`}><Trash2 size={13} /></button>}
        </>}
      </td>
    </tr>
  );
};

const Totals = ({ d }) => (
  <div className="ledger-totals" data-testid="ledger-totals">
    <div><span>Devreden bakiye</span><b data-testid="ledger-opening">{money2(d.opening)}</b></div>
    <div><span>Peşin satış / gelir</span><b className="pos" data-testid="ledger-income">{money2(d.income)}</b></div>
    <div><span>Vadeli satış (hesaba)</span><b data-testid="ledger-credit-sale">{money2(d.credit_sale)}</b></div>
    <div><span>Tahsilat</span><b className="pos" data-testid="ledger-collection">{money2(d.collection)}</b></div>
    <div><span>Gider ve ödemeler</span><b className="neg" data-testid="ledger-expense">{money2(d.expense)}</b></div>
    <div className="closing"><span>Gün sonu kasa</span><b data-testid="ledger-closing">{money2(d.closing)}</b></div>
  </div>
);

const CategorySummary = ({ rows }) => {
  const groups = Object.values(rows.reduce((acc, r) => {
    const key = r.category || "Genel";
    acc[key] = acc[key] || { name: key, total: 0, count: 0, out: isOut(r.type) };
    acc[key].total += r.amount; acc[key].count += 1;
    return acc;
  }, {})).sort((a, b) => b.total - a.total);
  if (!groups.length) return null;
  return (
    <section className="panel cat-summary" data-testid="ledger-category-summary">
      <h3>Günün kategori dağılımı</h3>
      <div className="cat-chips">{groups.map(g => <div key={g.name} className={`cat-chip ${g.out ? "out" : "in"}`} data-testid={`ledger-cat-${g.name}`}><span>{g.name}</span><b>{money2(g.total)}</b><small>{g.count} işlem</small></div>)}</div>
    </section>
  );
};

export default function LedgerPage({ lookups, onChange }) {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [days, setDays] = useState([]);
  const latest = useRef(date);
  const load = async () => {
    latest.current = date;
    const start = weekStart(date);
    const [l, d] = await Promise.all([api.get("/ledger", { params: { date } }), api.get("/ledger/days", { params: { start, end: addDays(start, 6) } })]);
    if (latest.current !== date || l.data.date !== date) return;
    setData(l.data); setDays(d.data);
  };
  useEffect(() => { setData(null); load(); }, [date]);
  const onSave = async (payload, id) => {
    try {
      if (id) await api.put(`/transactions/${id}`, { ...payload, date: data.rows.find(r => r.id === id)?.date || date }); else await api.post("/transactions", { ...payload, date });
      toast.success(id ? "Satır güncellendi" : "Satır eklendi"); await load(); onChange?.(); return true;
    } catch (e) { toast.error(errorText(e)); return false; }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Satır silinsin mi?")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Satır silindi"); load(); onChange?.(); } catch (e) { toast.error(errorText(e)); }
  };
  const exportDay = async () => { try { await downloadReport({ report: "transactions", fmt: "xlsx", start: date, end: date }); } catch { toast.error("Dışa aktarılamadı"); } };
  return (
    <div data-testid="ledger-page">
      <div className="ledger-head">
        <div className="ledger-date"><CalendarDays size={18} /><h2 data-testid="ledger-date-title">{fmtDay(date, LONG)}</h2></div>
        <div className="report-filters">
          <button className="outline-btn compact" onClick={() => setDate(today())} data-testid="ledger-today-button">Bugün</button>
          <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} data-testid="ledger-date-input" />
          <button className="outline-btn compact" onClick={exportDay} data-testid="ledger-export-button"><FileSpreadsheet size={15} /> Excel</button>
        </div>
      </div>
      <DayStrip date={date} setDate={setDate} days={days} />
      <section className="panel ledger-sheet">
        <div className="table-scroll"><table className="ledger-table" data-testid="ledger-table">
          <thead><tr><th>#</th><th>Tür</th><th>Açıklama</th><th className="num">Gelir</th><th className="num">Gider</th><th>İşlem kategorisi</th><th>Cari</th><th>Ödeme şekli</th><th>Not</th><th /></tr></thead>
          <tbody>
            {data?.rows.map((r, i) => <LedgerRow key={`${r.id}-${r.amount}-${r.title}`} row={r} index={i} lookups={lookups} onSave={onSave} onDelete={onDelete} onContactAdded={onChange} />)}
            {data && <LedgerRow key={`new-${date}`} lookups={lookups} onSave={onSave} onContactAdded={onChange} />}
          </tbody>
        </table></div>
        <p className="ledger-hint">Peşin satış kasaya girer · Vadeli satış müşterinin hesabına borç yazar (kasaya girmez) · Tahsilat müşteri borcunu düşer ve kasaya girer · Vadeli alış tedarikçiye borç yazar · Tedarikçi ödemesi borcumuzu düşer · Personel avansı personelin hesabına yazılır.<br />Hücreye yazın, <kbd>Enter</kbd> ile kaydedin · <kbd>Tab</kbd> sonraki hücre · <kbd>Esc</kbd> değişikliği geri al</p>
      </section>
      {data ? <><Totals d={data} /><CategorySummary rows={data.rows} /></> : <div className="panel empty-module">Defter yükleniyor...</div>}
    </div>
  );
}
